import React from 'react';
import { Activity, Droplet, LineChart } from 'lucide-react';
import { LineChart as RechartsLineChart, Line, ResponsiveContainer, YAxis, Tooltip, ReferenceLine } from 'recharts';

const extractVal = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === 'object') return val.value ?? val.val ?? val.current ?? null;
  return val;
};

export default function MetabolismCard({ metabolismData, sourceLabel }) {
  if (!metabolismData) return null;

  const { glucose, timeInRange, history } = metabolismData;

  const glucoseVal = extractVal(glucose?.current);
  const timeInRangeVal = extractVal(timeInRange);

  const isOptimal = glucoseVal != null && glucoseVal >= 70 && glucoseVal <= 140;
  const glucoseColor = glucoseVal == null ? 'var(--text-secondary)' : (isOptimal ? 'var(--health-green)' : (glucoseVal < 70 ? '#ef4444' : '#f59e0b'));

  return (
    <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 className="text-muted text-sm text-gradient-health" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
          <Activity size={16} /> Metabolismo (CGM)
        </h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.65rem', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
            Próximamente
          </span>
          <span style={{ fontSize: '0.65rem', background: 'rgba(56,189,248,0.15)', color: '#38bdf8', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
            {sourceLabel || 'Supersapiens'}
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
        
        {/* GLUCOSA ACTUAL */}
        <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '12px', border: `1px solid rgba(255,255,255,0.05)` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, textTransform: 'uppercase' }}>
              <Droplet size={12} color={glucoseColor} /> Glucosa
            </span>
            <span style={{ fontSize: '0.7rem', color: glucoseColor, fontWeight: 700 }}>
              {glucoseVal == null ? 'Sin datos' : (isOptimal ? 'Estable' : (glucoseVal < 70 ? 'Hipoglucemia' : 'Pico'))}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
            <span style={{ fontSize: '2.5rem', fontWeight: 800, color: 'white', lineHeight: 1 }}>{glucoseVal != null ? glucoseVal : '--'}</span>
            <span style={{ fontSize: '0.85rem', color: glucoseColor, fontWeight: 600 }}>mg/dL</span>
          </div>
        </div>

        {/* TIEMPO EN RANGO */}
        <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, textTransform: 'uppercase' }}>
              <LineChart size={12} color="#10b981" /> Tiempo en Rango
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
            <span style={{ fontSize: '2.5rem', fontWeight: 800, color: 'white', lineHeight: 1 }}>{timeInRangeVal != null ? timeInRangeVal : '--'}</span>
            <span style={{ fontSize: '0.85rem', color: '#10b981', fontWeight: 600 }}>%</span>
          </div>
          <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden', marginTop: '0.6rem' }}>
            <div style={{ height: '100%', width: `${timeInRangeVal || 0}%`, background: 'linear-gradient(90deg, #10b981, #00e5ff)' }} />
          </div>
        </div>

      </div>

      {/* TENDENCIA GLUCOSA (Últimas horas) */}
      {history && history.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          <p className="text-xs text-muted" style={{ marginBottom: '0.5rem' }}>Curva de Glucosa (Últimas 12h)</p>
          <div style={{ height: '60px', width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <RechartsLineChart data={history} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
                <YAxis domain={[40, 200]} hide />
                <Tooltip contentStyle={{ background: '#111827', border: 'none', borderRadius: '8px', fontSize: '12px' }} />
                {/* Zona óptima (70 - 140) */}
                <ReferenceLine y={140} stroke="rgba(255,255,255,0.1)" strokeDasharray="3 3" />
                <ReferenceLine y={70} stroke="rgba(255,255,255,0.1)" strokeDasharray="3 3" />
                
                <Line type="monotone" dataKey="value" stroke={glucoseColor} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </RechartsLineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
